import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { uploadMediaFile, uploadCourseVideo } from '@/hooks/useUpload';

// ============================================================
// Superadmin — Détail d'un cours de formation.
// Affiche les statistiques d'inscription (par période) et permet de
// modifier le cours : titre, description, prix, durée, vidéo YouTube
// ou fichier vidéo, PDF, image de couverture, statut.
// ============================================================

interface Formation {
  id: number;
  titre: string;
  description: string | null;
  description_long: string | null;
  image: string | null;
  prix: number | null;
  duree: string | null;
  categorie: string | null;
  souscategorie: string | null;
  status: string | null;
  video_url: string | null;
  pdf_url: string | null;
  created_at: string | null;
}

interface AccessRow {
  id: number;
  idcommerce: number | null;
  iduser: number | null;
  status: string | null;
  granted_at: string | null;
  revoked_at: string | null;
}

interface UserLite { id: number; name: string | null; user_name: string | null; email: string | null; nomcommerce: string | null; }

const inputClass = 'w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:border-primary-300 transition-colors';
const labelClass = 'block text-xs font-medium text-foreground-600 mb-1';

export default function SuperAdminFormationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const formationId = Number(id);

  const [formation, setFormation] = useState<Formation | null>(null);
  const [form, setForm] = useState<Formation | null>(null);
  const [access, setAccess] = useState<AccessRow[]>([]);
  const [users, setUsers] = useState<Record<number, UserLite>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [period, setPeriod] = useState<'7' | '30' | 'all'>('30');

  const load = useCallback(async () => {
    if (!formationId) return;
    setLoading(true);
    try {
      const [fRes, aRes] = await Promise.all([
        supabase.from('formations').select('*').eq('id', formationId).maybeSingle(),
        supabase.from('cours_access').select('id, idcommerce, iduser, status, granted_at, revoked_at').eq('cours_id', formationId).order('granted_at', { ascending: false }),
      ]);
      const f = (fRes.data as Formation) || null;
      setFormation(f);
      setForm(f);
      const rows = (aRes.data as AccessRow[]) || [];
      setAccess(rows);

      const userIds = Array.from(new Set(rows.map((r) => r.iduser).filter((v): v is number => v != null)));
      if (userIds.length > 0) {
        const { data: userRows } = await supabase
          .from('users')
          .select('id, name, user_name, email, nomcommerce')
          .in('id', userIds);
        const map: Record<number, UserLite> = {};
        (userRows as UserLite[] || []).forEach((u) => { map[u.id] = u; });
        setUsers(map);
      }
    } catch (err) {
      setMsg({ type: 'error', text: err instanceof Error ? err.message : 'Erreur de chargement' });
    } finally {
      setLoading(false);
    }
  }, [formationId]);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const nowLimit = period === 'all'
      ? null
      : new Date(Date.now() - parseInt(period, 10) * 24 * 60 * 60 * 1000);

    const inPeriod = access.filter((a) => {
      if (!nowLimit) return true;
      if (!a.granted_at) return false;
      return new Date(a.granted_at) >= nowLimit;
    });

    const active = access.filter((a) => a.status !== 'revoked');
    const activeInPeriod = inPeriod.filter((a) => a.status !== 'revoked');
    const uniqueCommerce = new Set(active.map((a) => a.idcommerce).filter(Boolean)).size;

    return {
      total: access.length,
      active: active.length,
      revoked: access.length - active.length,
      inPeriod: inPeriod.length,
      activeInPeriod: activeInPeriod.length,
      uniqueCommerce,
    };
  }, [access, period]);

  const monthlyBuckets = useMemo(() => {
    const buckets: Record<string, number> = {};
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      buckets[`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`] = 0;
    }
    access.forEach((a) => {
      if (!a.granted_at) return;
      const d = new Date(a.granted_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (buckets[key] !== undefined) buckets[key] += 1;
    });
    const max = Math.max(1, ...Object.values(buckets));
    return Object.entries(buckets).map(([key, value]) => ({
      key,
      label: new Date(`${key}-01`).toLocaleDateString('fr-FR', { month: 'short' }),
      value,
      pct: Math.round((value / max) * 100),
    }));
  }, [access]);

  const save = async () => {
    if (!form) return;
    setSaving(true);
    setMsg(null);
    try {
      const { error } = await supabase.from('formations').update({
        titre: form.titre?.trim(),
        description: form.description,
        description_long: form.description_long,
        image: form.image,
        prix: form.prix,
        duree: form.duree,
        categorie: form.categorie,
        souscategorie: form.souscategorie,
        status: form.status,
        video_url: form.video_url,
        pdf_url: form.pdf_url,
      }).eq('id', formationId);
      if (error) throw error;
      setFormation(form);
      setMsg({ type: 'success', text: 'Cours mis à jour.' });
    } catch (err) {
      setMsg({ type: 'error', text: err instanceof Error ? err.message : 'Erreur lors de l’enregistrement' });
    } finally {
      setSaving(false);
    }
  };

  const handleVideoUpload = async (file: File) => {
    setVideoUploading(true);
    setMsg(null);
    try {
      const url = await uploadCourseVideo(file);
      setForm((f) => (f ? { ...f, video_url: url } : f));
    } catch (err) {
      setMsg({ type: 'error', text: err instanceof Error ? err.message : 'Erreur de téléversement de la vidéo' });
    } finally {
      setVideoUploading(false);
    }
  };

  const handleUpload = async (kind: 'pdf' | 'image', file: File) => {
    setUploading(true);
    setMsg(null);
    try {
      const url = await uploadMediaFile(file, 'formations');
      setForm((f) => (f ? { ...f, [kind === 'pdf' ? 'pdf_url' : 'image']: url } : f));
    } catch (err) {
      setMsg({ type: 'error', text: err instanceof Error ? err.message : 'Erreur de téléversement' });
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return <div className="p-4 md:p-6 flex items-center justify-center py-20"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>;
  }

  if (!formation || !form) {
    return (
      <div className="p-4 md:p-6">
        <p className="text-sm text-foreground-500">Cours introuvable.</p>
        <button onClick={() => navigate('/superadmin/formations')} className="mt-4 px-4 py-2 rounded-full bg-foreground-950 text-background-50 text-sm cursor-pointer">Retour</button>
      </div>
    );
  }

  const isYoutube = !!form.video_url && /youtu/.test(form.video_url);

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate('/superadmin/formations')} className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center hover:bg-background-200/70 transition-colors cursor-pointer flex-shrink-0">
            <i className="ri-arrow-left-line text-foreground-600"></i>
          </button>
          <div className="min-w-0">
            <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-950 truncate">{formation.titre}</h2>
            <p className="text-xs text-foreground-500 truncate">
              {formation.categorie}{formation.souscategorie ? ` › ${formation.souscategorie}` : ''}
            </p>
          </div>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer"
        >
          {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
          Enregistrer
        </button>
      </div>

      {msg && (
        <div className={`mb-5 px-4 py-3 rounded-lg text-sm font-medium ${msg.type === 'success' ? 'bg-accent-50 text-accent-700' : 'bg-red-50 text-red-600'}`}>
          {msg.text}
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Inscrits (total)', value: stats.total, icon: 'ri-user-add-line', color: 'bg-primary-100 text-primary-700' },
          { label: 'Actifs', value: stats.active, icon: 'ri-user-follow-line', color: 'bg-accent-100 text-accent-700' },
          { label: 'Nouveaux (période)', value: stats.inPeriod, icon: 'ri-calendar-check-line', color: 'bg-secondary-100 text-secondary-700' },
          { label: 'Commerces distincts', value: stats.uniqueCommerce, icon: 'ri-store-2-line', color: 'bg-background-200/60 text-foreground-700' },
        ].map((s) => (
          <div key={s.label} className="bg-background-50 border border-background-200/70 rounded-lg p-4">
            <div className={`w-8 h-8 rounded-lg ${s.color} flex items-center justify-center mb-2`}><i className={`${s.icon} text-sm`}></i></div>
            <div className="text-xl font-bold font-heading text-foreground-950">{s.value}</div>
            <div className="text-xs text-foreground-500">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ===== Édition ===== */}
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-5 space-y-4">
          <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-2"><i className="ri-edit-line text-primary-500"></i>Informations du cours</h3>

          <div>
            <label className={labelClass}>Titre</label>
            <input className={inputClass} value={form.titre || ''} onChange={(e) => setForm({ ...form, titre: e.target.value })} />
          </div>
          <div>
            <label className={labelClass}>Description courte</label>
            <textarea className={`${inputClass} resize-none`} rows={2} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div>
            <label className={labelClass}>Description détaillée</label>
            <textarea className={`${inputClass} resize-none`} rows={4} value={form.description_long || ''} onChange={(e) => setForm({ ...form, description_long: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Prix (MAD)</label>
              <input type="number" className={inputClass} value={form.prix ?? 0} onChange={(e) => setForm({ ...form, prix: parseFloat(e.target.value) || 0 })} />
            </div>
            <div>
              <label className={labelClass}>Durée</label>
              <input className={inputClass} value={form.duree || ''} onChange={(e) => setForm({ ...form, duree: e.target.value })} placeholder="ex : 2 h 30" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Catégorie</label>
              <input className={inputClass} value={form.categorie || ''} onChange={(e) => setForm({ ...form, categorie: e.target.value })} />
            </div>
            <div>
              <label className={labelClass}>Sous-catégorie</label>
              <input className={inputClass} value={form.souscategorie || ''} onChange={(e) => setForm({ ...form, souscategorie: e.target.value })} />
            </div>
          </div>
          <div>
            <label className={labelClass}>Statut</label>
            <select className={inputClass} value={form.status || 'published'} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="published">Publié</option>
              <option value="draft">Brouillon</option>
              <option value="archived">Archivé</option>
            </select>
          </div>
        </div>

        {/* ===== Médias ===== */}
        <div className="space-y-6">
          <div className="bg-background-50 border border-background-200/70 rounded-xl p-5 space-y-4">
            <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-2"><i className="ri-play-circle-line text-accent-500"></i>Vidéo &amp; support</h3>
            <div>
              <label className={labelClass}>Vidéo (lien YouTube ou fichier uploadé)</label>
              <input className={inputClass} value={form.video_url || ''} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="Collez un lien YouTube, ou téléversez un fichier ci-dessous" />
              <label className="mt-2 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-dashed border-background-300/60 bg-background-50 text-xs text-foreground-600 cursor-pointer whitespace-nowrap">
                {videoUploading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-upload-2-line"></i>}
                {videoUploading ? 'Téléversement…' : 'Téléverser une vidéo depuis mon appareil'}
                <input type="file" accept="video/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleVideoUpload(f); e.target.value = ''; }} />
              </label>
              {isYoutube && (
                <div className="mt-2 aspect-video rounded-lg overflow-hidden border border-background-200/70 bg-background-100">
                  <iframe
                    title="Aperçu vidéo"
                    className="w-full h-full"
                    src={`https://www.youtube.com/embed/${(form.video_url || '').match(/(?:v=|youtu\.be\/)([\w-]+)/)?.[1] || ''}`}
                    allowFullScreen
                  />
                </div>
              )}
              {form.video_url && !isYoutube && (
                <div className="mt-2 aspect-video rounded-lg overflow-hidden border border-background-200/70 bg-background-100">
                  <video src={form.video_url} controls className="w-full h-full" />
                </div>
              )}
            </div>
            <div>
              <label className={labelClass}>Fichier PDF (optionnel)</label>
              <div className="flex items-center gap-2">
                <input className={inputClass} value={form.pdf_url || ''} onChange={(e) => setForm({ ...form, pdf_url: e.target.value })} placeholder="URL du PDF" />
                <label className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg border border-dashed border-background-300/60 bg-background-50 text-xs text-foreground-600 cursor-pointer whitespace-nowrap">
                  <i className="ri-upload-2-line"></i>Téléverser
                  <input type="file" accept="application/pdf" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleUpload('pdf', f); e.target.value = ''; }} />
                </label>
              </div>
            </div>
            <div>
              <label className={labelClass}>Image de couverture</label>
              <div className="flex items-center gap-2">
                <input className={inputClass} value={form.image || ''} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="URL de l’image" />
                <label className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg border border-dashed border-background-300/60 bg-background-50 text-xs text-foreground-600 cursor-pointer whitespace-nowrap">
                  <i className="ri-image-line"></i>Téléverser
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleUpload('image', f); e.target.value = ''; }} />
                </label>
              </div>
              {form.image && (
                <div className="mt-2 w-full h-32 rounded-lg overflow-hidden border border-background-200/70 bg-background-100">
                  <img src={form.image} alt="Couverture" className="w-full h-full object-cover" />
                </div>
              )}
            </div>
            {uploading && <p className="text-[11px] text-foreground-400"><i className="ri-loader-4-line animate-spin mr-1"></i>Téléversement…</p>}
          </div>

          {/* ===== Inscriptions ===== */}
          <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-2"><i className="ri-bar-chart-line text-secondary-700"></i>Inscriptions</h3>
              <div className="flex items-center gap-0.5 bg-background-100 rounded-full p-0.5">
                {(['7', '30', 'all'] as const).map((p) => (
                  <button key={p} onClick={() => setPeriod(p)} className={`px-3 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors cursor-pointer ${period === p ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500'}`}>
                    {p === 'all' ? 'Tout' : `${p} j`}
                  </button>
                ))}
              </div>
            </div>

            {/* histogramme 6 mois */}
            <div className="h-32 flex items-end gap-2 mb-4">
              {monthlyBuckets.map((m) => (
                <div key={m.key} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-[10px] text-foreground-400">{m.value}</span>
                  <div className="w-full bg-background-200/70 rounded-t-md" style={{ height: `${Math.max(4, m.pct)}%` }}>
                    <div className="w-full h-full bg-primary-500 rounded-t-md" />
                  </div>
                  <span className="text-[10px] text-foreground-400 capitalize">{m.label}</span>
                </div>
              ))}
            </div>

            <div className="space-y-2 max-h-56 overflow-y-auto">
              {access.length === 0 ? (
                <p className="text-xs text-foreground-400 py-4 text-center">Aucune inscription pour ce cours.</p>
              ) : access.slice(0, 40).map((a) => {
                const u = a.iduser != null ? users[a.iduser] : null;
                return (
                  <div key={a.id} className="flex items-center justify-between gap-2 py-1.5 border-b border-background-200/70 last:border-0">
                    <div className="min-w-0">
                      <p className="text-xs text-foreground-800 truncate">{u ? (u.nomcommerce || u.name || u.user_name || `#${a.iduser}`) : `Utilisateur #${a.iduser}`}</p>
                      <p className="text-[10px] text-foreground-400 truncate">{u?.email || ''}</p>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${a.status === 'revoked' ? 'bg-red-50 text-red-600' : 'bg-accent-50 text-accent-700'}`}>
                      {a.status === 'revoked' ? 'Révoqué' : 'Actif'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}